import { ChainData, ChainType } from "@0xsquid/squid-types";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useBalance as useWagmiBalance } from "wagmi";
import { useCosmosContext } from "../../core";
import { CHAIN_IDS } from "../../core/constants";
import { keys } from "../../core/queries/queries-keys";
import {
  formatBNToReadable,
  isWalletAddressValid,
  parseToBigInt,
} from "../../services";
import {
  getBitcoinNativeBalance,
  getSolanaNativeBalance,
} from "../../services/external/rpcService";
import { scaleWeiToHbar } from "../../services/internal/hederaService";
import { useCosmosForChain } from "../cosmos/useCosmosForChain";
import { useWallet } from "../wallet/useWallet";
import {
  useCantonBalance,
  useCosmosBalance,
  useStellarBalance,
  useSuiBalance,
  useXrplBalance,
} from "./useBalance";
import { useNativeTokenForChain } from "./useNativeTokenForChain";

interface Balance {
  decimals: number;
  value: bigint;
}

interface UseNativeBalanceResult {
  balance: Balance | undefined;
  isLoading: boolean;
}

interface UseNativeBalanceOptions {
  chain: ChainData | undefined;
  address: string | undefined;
}

export const useEvmNativeBalance = ({
  address,
  chain,
}: UseNativeBalanceOptions): UseNativeBalanceResult => {
  const { isChainTypeConnected } = useWallet();

  const { data: nativeEvmBalance, isLoading } = useWagmiBalance({
    address: address as `0x${string}`,
    chainId: Number(chain?.chainId),
    query: {
      enabled: isChainTypeConnected(ChainType.EVM) && !!address,
      refetchInterval: 10000,
      staleTime: 5000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    },
  });

  const balance = useMemo(() => {
    if (nativeEvmBalance?.decimals != null && nativeEvmBalance?.value != null) {
      if (chain?.chainId === CHAIN_IDS.HEDERA) {
        return {
          decimals: 8,
          value: scaleWeiToHbar(nativeEvmBalance.value),
        };
      }

      return {
        decimals: nativeEvmBalance.decimals,
        value: nativeEvmBalance.value,
      };
    }

    return;
  }, [nativeEvmBalance?.decimals, nativeEvmBalance?.value, chain?.chainId]);

  return {
    balance,
    isLoading,
  };
};

export const useCosmosNativeBalance = ({
  address,
  chain,
}: UseNativeBalanceOptions): UseNativeBalanceResult => {
  const { isConnected: isCosmosConnected } = useCosmosContext();
  const { nativeToken: nativeCosmosToken } = useNativeTokenForChain(chain);

  const { balance: rawBalance, isLoading } = useCosmosBalance({
    chain,
    token: nativeCosmosToken,
    userAddress: address,
    enabled: isCosmosConnected && chain?.chainType === ChainType.COSMOS,
    refreshIntervalMs: 10000,
  });

  const balance = useMemo(() => {
    if (nativeCosmosToken?.decimals && rawBalance) {
      return {
        decimals: nativeCosmosToken.decimals,
        value: parseToBigInt(rawBalance, nativeCosmosToken.decimals),
      };
    }
  }, [nativeCosmosToken?.decimals, rawBalance]);

  return { balance, isLoading };
};

export const useBitcoinNativeBalance = ({
  chain,
  address,
}: UseNativeBalanceOptions): UseNativeBalanceResult => {
  const { nativeToken } = useNativeTokenForChain(chain);

  const { data: rawBalance = "0", isLoading } = useQuery(
    keys().balance(chain?.chainId, nativeToken?.address, address),
    async () => {
      if (!address || !nativeToken) return "0";

      const balance = await getBitcoinNativeBalance(address);

      return formatBNToReadable(balance, nativeToken.decimals);
    },
    {
      enabled:
        chain?.chainType === ChainType.BTC &&
        nativeToken?.decimals != null &&
        isWalletAddressValid(chain, address),
      refetchInterval: 40000,
      staleTime: 20000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );

  const balance = useMemo(() => {
    if (nativeToken?.decimals && rawBalance) {
      return {
        decimals: nativeToken.decimals,
        value: parseToBigInt(rawBalance, nativeToken.decimals),
      };
    }
  }, [nativeToken?.decimals, rawBalance]);

  return {
    balance,
    isLoading,
  };
};

export const useSolanaNativeBalance = ({
  chain,
  address,
}: UseNativeBalanceOptions): UseNativeBalanceResult => {
  const { nativeToken } = useNativeTokenForChain(chain);
  const { data: rawBalance, isLoading } = useQuery(
    keys().balance(chain?.chainId, nativeToken?.address, address),
    async () => {
      const balance = await getSolanaNativeBalance(address!);

      return formatBNToReadable(balance, nativeToken!.decimals);
    },
    {
      enabled:
        !!address &&
        nativeToken?.decimals != null &&
        chain?.chainType === ChainType.SOLANA,
      refetchInterval: 40000,
      staleTime: 20000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  );

  const balance = useMemo(() => {
    if (nativeToken?.decimals && rawBalance) {
      return {
        decimals: nativeToken.decimals,
        value: parseToBigInt(rawBalance, nativeToken.decimals),
      };
    }
  }, [nativeToken?.decimals, rawBalance]);

  return {
    balance,
    isLoading,
  };
};

export const useSuiNativeBalance = ({
  address,
  chain,
}: UseNativeBalanceOptions): UseNativeBalanceResult => {
  const { nativeToken } = useNativeTokenForChain(chain);

  const { balance: rawBalance, isLoading } = useSuiBalance({
    chain,
    token: nativeToken,
    userAddress: address,
  });

  const balance = useMemo(() => {
    if (nativeToken?.decimals && rawBalance) {
      return {
        decimals: nativeToken.decimals,
        value: parseToBigInt(rawBalance, nativeToken.decimals),
      };
    }
  }, [nativeToken?.decimals, rawBalance]);

  return {
    balance,
    isLoading,
  };
};

export const useXrplNativeBalance = ({
  address,
  chain,
}: UseNativeBalanceOptions): UseNativeBalanceResult => {
  const { nativeToken } = useNativeTokenForChain(chain);
  const { balance: rawBalance, isLoading } = useXrplBalance({
    chain,
    token: nativeToken,
    userAddress: address,
    enabled: chain?.chainType === ChainType.XRPL,
  });

  const balance = useMemo(() => {
    if (nativeToken?.decimals && rawBalance) {
      return {
        decimals: nativeToken.decimals,
        value: parseToBigInt(rawBalance, nativeToken.decimals),
      };
    }
  }, [nativeToken?.decimals, rawBalance]);

  return {
    balance,
    isLoading,
  };
};

export const useStellarNativeBalance = ({
  address,
  chain,
}: UseNativeBalanceOptions): UseNativeBalanceResult => {
  const { nativeToken } = useNativeTokenForChain(chain);
  const { balance: rawBalance, isLoading } = useStellarBalance({
    chain,
    token: nativeToken,
    userAddress: address,
    enabled: chain?.chainType === ChainType.STELLAR,
  });

  const balance = useMemo(() => {
    if (nativeToken?.decimals && rawBalance) {
      return {
        decimals: nativeToken.decimals,
        value: parseToBigInt(rawBalance, nativeToken.decimals),
      };
    }
  }, [nativeToken?.decimals, rawBalance]);

  return {
    balance,
    isLoading,
  };
};

export const useCantonNativeBalance = ({
  address,
  chain,
}: UseNativeBalanceOptions): UseNativeBalanceResult => {
  const { nativeToken } = useNativeTokenForChain(chain);
  const { balance: rawBalance, isLoading } = useCantonBalance({
    chain,
    token: nativeToken,
    userAddress: address,
    enabled: chain?.chainType === ChainType.CANTON,
  });

  const balance = useMemo(() => {
    if (nativeToken?.decimals && rawBalance) {
      return {
        decimals: nativeToken.decimals,
        value: parseToBigInt(rawBalance, nativeToken.decimals),
      };
    }
  }, [nativeToken?.decimals, rawBalance]);

  return { balance, isLoading };
};

export const useNativeBalance = (chain?: ChainData) => {
  const { connectedAddresses } = useWallet();
  const { data: cosmosAddressForChain } = useCosmosForChain(chain);

  // Cosmos is a special case because the address changes on every chain
  // so we can't use the default cosmos connected address
  const { balance: nativeCosmosBalance, isLoading: isCosmosLoading } =
    useCosmosNativeBalance({
      address: cosmosAddressForChain,
      chain,
    });

  const { balance: nativeEvmBalance, isLoading: isEvmLoading } =
    useEvmNativeBalance({ address: connectedAddresses[ChainType.EVM], chain });

  const { balance: nativeBitcoinBalance, isLoading: isBitcoinLoading } =
    useBitcoinNativeBalance({
      address: connectedAddresses[ChainType.BTC],
      chain,
    });

  const { balance: nativeSolanaBalance, isLoading: isSolanaLoading } =
    useSolanaNativeBalance({
      address: connectedAddresses[ChainType.SOLANA],
      chain,
    });

  const { balance: nativeSuiBalance, isLoading: isSuiLoading } =
    useSuiNativeBalance({
      address: connectedAddresses[ChainType.SUI],
      chain,
    });

  const { balance: nativeXrplBalance, isLoading: isXrpLoading } =
    useXrplNativeBalance({
      address: connectedAddresses[ChainType.XRPL],
      chain,
    });

  const { balance: nativeStellarBalance, isLoading: isStellarLoading } =
    useStellarNativeBalance({
      address: connectedAddresses[ChainType.STELLAR],
      chain,
    });

  const { balance: nativeCantonBalance, isLoading: isCantonLoading } =
    useCantonNativeBalance({
      address: connectedAddresses[ChainType.CANTON],
      chain,
    });

  const { nativeBalance, nativeBalanceFormatted } = useMemo(() => {
    let balance: Balance | undefined;
    switch (chain?.chainType) {
      case ChainType.EVM:
        balance = nativeEvmBalance;
        break;
      case ChainType.COSMOS:
        balance = nativeCosmosBalance;
        break;
      case ChainType.BTC:
        balance = nativeBitcoinBalance;
        break;
      case ChainType.SOLANA:
        balance = nativeSolanaBalance;
        break;
      case ChainType.SUI:
        balance = nativeSuiBalance;
        break;
      case ChainType.XRPL:
        balance = nativeXrplBalance;
        break;
      case ChainType.STELLAR:
        balance = nativeStellarBalance;
        break;
      case ChainType.CANTON:
        balance = nativeCantonBalance;
        break;
    }

    const balanceFormatted = !!balance
      ? formatBNToReadable(balance.value, balance.decimals)
      : undefined;

    return {
      nativeBalance: balance,
      nativeBalanceFormatted: balanceFormatted,
    };
  }, [
    chain?.chainType,
    nativeEvmBalance,
    nativeCosmosBalance,
    nativeBitcoinBalance,
    nativeSolanaBalance,
    nativeSuiBalance,
    nativeXrplBalance,
    nativeStellarBalance,
    nativeCantonBalance,
  ]);

  const isLoading = useMemo((): boolean => {
    if (!chain?.chainType) return false;

    switch (chain.chainType) {
      case ChainType.EVM:
        return isEvmLoading;
      case ChainType.COSMOS:
        return isCosmosLoading;
      case ChainType.BTC:
        return isBitcoinLoading;
      case ChainType.SOLANA:
        return isSolanaLoading;
      case ChainType.SUI:
        return isSuiLoading;
      case ChainType.XRPL:
        return isXrpLoading;
      case ChainType.STELLAR:
        return isStellarLoading;
      case ChainType.CANTON:
        return isCantonLoading;
    }
  }, [
    chain?.chainType,
    isEvmLoading,
    isCosmosLoading,
    isBitcoinLoading,
    isSolanaLoading,
    isSuiLoading,
    isXrpLoading,
    isStellarLoading,
    isCantonLoading,
  ]);

  return { nativeBalance, nativeBalanceFormatted, isLoading };
};
